<script lang="ts">
  import { onMount } from 'svelte';
  import { onVisibilityChange, pageVisible } from '../platform/page-lifecycle';
  import { getAppearance } from './appearance.svelte';
  const appearance = getAppearance();

  let x = $state(0);
  let y = $state(0);
  let hidden = $state(false);
  let frame = 0;
  const still = $derived(!appearance.motionAllowed || hidden);
  // Recentre when motion stops or the theme changes.
  $effect(() => {
    void still;
    void appearance.theme.id;
    x = 0;
    y = 0;
  });

  onMount(() => {
    const visibility = (visible: boolean) => {
      hidden = !visible;
      document.documentElement.setAttribute('data-page-hidden', String(hidden));
    };
    const move = (event: PointerEvent) => {
      if (
        still ||
        event.buttons !== 0 ||
        event.pointerType !== 'mouse' ||
        !appearance.theme.background.length
      )
        return;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (still) return;
        x = (event.clientX / window.innerWidth) * 2 - 1;
        y = (event.clientY / window.innerHeight) * 2 - 1;
      });
    };
    const reset = () => {
      cancelAnimationFrame(frame);
      x = 0;
      y = 0;
    };
    visibility(pageVisible());
    const stopVisibility = onVisibilityChange(visibility);
    window.addEventListener('pointermove', move, { passive: true });
    window.addEventListener('blur', reset);
    return () => {
      cancelAnimationFrame(frame);
      stopVisibility();
      window.removeEventListener('pointermove', move);
      window.removeEventListener('blur', reset);
      document.documentElement.removeAttribute('data-page-hidden');
    };
  });
</script>

<div class="theme-background" aria-hidden="true" class:still>
  {#key appearance.theme.id}
    {#each appearance.theme.background as layer}
      <div
        class="layer"
        style:transform={`translate(${x * (layer.depth ?? 0)}px, ${y * (layer.depth ?? 0)}px)`}
        style:opacity={layer.opacity ?? 1}
      >
        <picture>
          {#if layer.mobileSrc}<source media="(max-width: 600px)" srcset={layer.mobileSrc} />{/if}
          <img
            src={layer.src}
            alt=""
            draggable="false"
            style:object-fit={layer.size ?? 'cover'}
            style:object-position={layer.position ?? 'center'}
            style:animation={layer.animation ?? 'none'}
            onerror={(event) => ((event.currentTarget as HTMLImageElement).style.visibility = 'hidden')}
          />
        </picture>
      </div>
    {/each}
  {/key}
</div>

<style>
  .theme-background {
    position: fixed;
    inset: 0;
    z-index: 0;
    overflow: hidden;
    pointer-events: none;
    filter: var(--theme-background-filter);
    contain: strict;
  }
  .layer {
    position: absolute;
    inset: -32px;
    transition: transform 700ms ease-out;
  }
  picture {
    display: block;
    width: 100%;
    height: 100%;
  }
  img {
    width: 100%;
    height: 100%;
  }
  .still .layer {
    transform: none !important;
    transition: none;
  }
  .still img {
    animation: none !important;
  }
</style>
